import { useCallback, useEffect, useState } from 'react';
import {
    ClubSocialOverviewInterface,
    SocialService,
} from 'services/social.service';

export interface UseSocialOverviewResult {
    /** Managed clubs + their Facebook connection state (empty until loaded). */
    overview: ClubSocialOverviewInterface[];
    loading: boolean;
    /** True when the last fetch failed. */
    error: boolean;
    /** Re-fetches the overview (used after connect / disconnect / reconnect). */
    refetch: () => void;
}

/**
 * Loads the social overview (managed clubs + their Facebook connection state)
 * via `SocialService.getOverview()`, following the `useClubsList` hook pattern.
 *
 * Shared by both social screens (composer + connections) so that after a
 * connect / disconnect / reconnect the UI can `refetch()` the overview instead
 * of doing a full `window.location.reload()` (design §6).
 */
export const useSocialOverview = (enabled = true): UseSocialOverviewResult => {
    const [overview, setOverview] = useState<ClubSocialOverviewInterface[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    const fetchOverview = useCallback((): void => {
        setLoading(true);
        setError(false);

        new SocialService()
            .getOverview()
            .then((response) => {
                setOverview(response?.data ?? []);
            })
            .catch(() => {
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        // `enabled=false` se používá např. v globálním menu, kde overview
        // (a badge počtu připojených stránek) dává smysl načítat jen pro
        // uživatele se sekcí Sociální sítě — ostatní zbytečně nefetchují.
        if (!enabled) {
            return;
        }
        fetchOverview();
    }, [fetchOverview, enabled]);

    return { overview, loading, error, refetch: fetchOverview };
};

export default useSocialOverview;
